// src/features/admin/components/settings/document-management/DocumentManagementPanel.tsx
import React, { useState, useEffect } from 'react';
import {
  FolderIcon,
  DocumentDuplicateIcon,
  ArchiveBoxIcon,
  CogIcon,
  DocumentTextIcon,
  CircleStackIcon
} from '@heroicons/react/24/outline';
import DocumentDashboard from './components/DocumentDashboard';
import DocumentTemplates from './components/DocumentTemplates';
import FontManager from './components/FontManager';
import ArchivedDocuments from './components/ArchivedDocuments';
import EmbeddingHealth from './components/EmbeddingHealth';

export interface DocumentManagementPanelProps {
  onSave?: (config: any) => void;
  onCancel?: () => void;
}

type DocumentConfigurationSection = 'dashboard' | 'templates' | 'font-manager' | 'archived-documents' | 'embedding-health';

export const DocumentManagementPanel: React.FC<DocumentManagementPanelProps> = () => {
  const [activeSection, setActiveSection] = useState<DocumentConfigurationSection>('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Mock API call for document management configuration
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // In production, this would load document management settings
      console.log('Document management panel initialized');
    } catch (err) {
      console.error('Failed to load document management data:', err);
      setError('Failed to load document management configuration');
    } finally {
      setLoading(false);
    }
  };

  // Handle configuration changes from child components
  // const handleConfigChange = useCallback((config: any) => {
  //   console.log('Document management config changed:', config);
  // }, []); // Reserved for future use

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Mock save operation
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Document management configuration saved');
    } catch (err) {
      console.error('Failed to save document management configuration:', err);
      setError('Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading && activeSection === 'dashboard') {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lex-accent mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading document management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="document-management-panel">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-600 pb-4 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center">
              <FolderIcon className="h-8 w-8 text-lex-accent mr-3" />
              Document Management
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Configure document templates, manage archives, and monitor document workflow across your firm.
            </p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={handleSave}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-lex-accent dark:border-lex-accent rounded-md text-sm font-medium text-lex-accent dark:text-lex-accent-contrast bg-lex-accent-soft dark:bg-lex-accent-soft hover:bg-lex-state-hover dark:hover:bg-lex-state-hover hover:border-lex-accent dark:hover:border-lex-accent transition-colors disabled:opacity-50"
              title="Save document management configuration"
            >
              <CogIcon className="h-4 w-4 mr-2" />
              Save Settings
            </button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
        </div>
      )}

      {/* Navigation tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {[
            { 
              id: 'dashboard', 
              label: 'Documents Dashboard',
              icon: FolderIcon
            },
            { 
              id: 'templates', 
              label: 'Document Templates',
              icon: DocumentDuplicateIcon
            },
            { 
              id: 'font-manager', 
              label: 'Font Manager',
              icon: DocumentTextIcon
            },
            {
              id: 'archived-documents',
              label: 'Archived Documents',
              icon: ArchiveBoxIcon
            },
            {
              id: 'embedding-health',
              label: 'Embedding Health',
              icon: CircleStackIcon
            }
          ].map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id as DocumentConfigurationSection)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeSection === tab.id
                    ? 'border-lex-accent text-lex-accent dark:text-lex-accent'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <IconComponent className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content sections */}
      <div className="min-h-[400px]">
        {activeSection === 'dashboard' && (
          <div className="pt-6">
            <DocumentDashboard />
          </div>
        )}

        {activeSection === 'templates' && (
          <div className="pt-6">
            <DocumentTemplates />
          </div>
        )}

        {activeSection === 'font-manager' && (
          <div className="pt-6">
            <FontManager />
          </div>
        )}

        {activeSection === 'archived-documents' && (
          <div className="pt-6">
            <ArchivedDocuments />
          </div>
        )}

        {activeSection === 'embedding-health' && (
          <div className="pt-6">
            <EmbeddingHealth />
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentManagementPanel;